import { useEffect, useState } from 'react';
import { Bell, BellOff, Loader2, Check, Send, Mail } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import {
  isPushSupported,
  enablePush,
  disablePush,
  fetchPushStatus,
  sendTestPush,
  getPushPreferences,
  updatePushPreferences,
  previewWeeklyDigest,
} from '../lib/push';

const DISMISS_KEY = 'selah_dismiss_push_prompt_v1';

/**
 * A slim, dismissible Dashboard card inviting the user to enable
 * 35-day dispute reminder pings. If already subscribed, quietly hides.
 */
export default function PushReminderCard() {
  const [supported, setSupported] = useState(false);
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    (async () => {
      if (!isPushSupported()) return;
      setSupported(true);
      const subscribed = await fetchPushStatus();
      if (subscribed) {
        setEnabled(true);
        return; // don't show the prompt if they've already opted in
      }
      if (localStorage.getItem(DISMISS_KEY)) return;
      // Only show if permission hasn't been explicitly denied at OS level
      if (typeof Notification !== 'undefined' && Notification.permission === 'denied') return;
      setVisible(true);
    })();
  }, []);

  const handleEnable = async () => {
    setBusy(true);
    try {
      await enablePush();
      setEnabled(true);
      toast.success('Reminders on. We\u2019ll ping you at day 30 and day 35.');
      // Fire a friendly test right after enabling
      try { await sendTestPush(); } catch { /* silent */ }
      setVisible(false);
    } catch (err) {
      const code = err?.code || '';
      if (code === 'permission-denied') {
        toast.error('Notifications were blocked. You can enable them from your browser settings.');
      } else {
        toast.error('Could not enable reminders on this device.');
      }
    } finally {
      setBusy(false);
    }
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, '1');
    setVisible(false);
  };

  if (!supported) return null;
  if (enabled) return null;
  if (!visible) return null;

  return (
    <div
      className="relative rounded-sm overflow-hidden border border-[#36454F]/12 bg-white shadow-sm mb-6 flex flex-col sm:flex-row items-stretch"
      data-testid="push-reminder-card"
    >
      <div
        className="hidden sm:flex w-14 flex-shrink-0 items-center justify-center border-r border-[#36454F]/10"
        style={{ background: 'linear-gradient(180deg,#F5F5F5 0%,#EDE4DF 100%)' }}
        aria-hidden="true"
      >
        <Bell className="w-5 h-5 text-[#B76E79]" strokeWidth={1.5} />
      </div>
      <div className="flex-1 p-4 sm:p-5">
        <div className="flex items-center mb-1">
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-['Manrope'] font-semibold tracking-wider uppercase text-[#B76E79] bg-[#B76E79]/10 mr-2">
            New · Push reminders
          </span>
        </div>
        <h3 className="font-['Playfair_Display'] text-lg font-semibold text-[#36454F] leading-snug mb-1">
          Never miss a 35-day window again.
        </h3>
        <p className="text-sm text-[#36454F]/70 font-['Manrope'] leading-relaxed mb-3 pr-6">
          Get a gentle ping at <strong>day 30</strong> (bureau should be responding) and <strong>day 35</strong> (time to escalate with a MOV letter or CFPB complaint). Ad-free, always dismissible.
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            onClick={handleEnable}
            disabled={busy}
            className="h-9 bg-[#B76E79] hover:bg-[#9e5c66] text-white font-['Manrope'] rounded-sm text-sm"
            data-testid="push-enable-btn"
          >
            {busy ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" strokeWidth={2} /> Enabling…</>
                  : <><Bell className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Enable reminders</>}
          </Button>
          <Button
            onClick={dismiss}
            variant="ghost"
            className="h-9 text-[#36454F]/60 hover:text-[#36454F] hover:bg-transparent font-['Manrope'] text-sm"
            data-testid="push-dismiss-btn"
          >
            Not now
          </Button>
        </div>
      </div>
    </div>
  );
}

/** A slim status pill for Settings (or wherever), showing enabled/off with a toggle. */
export function PushReminderToggle() {
  const [supported, setSupported] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      if (!isPushSupported()) return;
      setSupported(true);
      setEnabled(await fetchPushStatus());
    })();
  }, []);

  if (!supported) {
    return (
      <div className="flex items-start p-3 bg-[#F5F5F5] rounded-sm" data-testid="push-unsupported">
        <BellOff className="w-5 h-5 text-[#36454F]/40 mr-3 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
        <div className="text-sm font-['Manrope'] text-[#36454F]/70">
          This browser doesn&apos;t support push notifications. Try Chrome, Safari 16.4+, Edge, or Firefox.
        </div>
      </div>
    );
  }

  const toggle = async () => {
    setBusy(true);
    try {
      if (enabled) {
        await disablePush();
        setEnabled(false);
        toast.success('Reminders turned off for this device.');
      } else {
        await enablePush();
        setEnabled(true);
        try { await sendTestPush(); } catch { /* silent */ }
        toast.success('Reminders turned on for this device.');
      }
    } catch (err) {
      if (err?.code === 'permission-denied') {
        toast.error('Notifications were blocked. Enable them from your browser settings.');
      } else {
        toast.error('Could not update reminder settings.');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-start justify-between p-4 border border-[#36454F]/10 rounded-sm bg-white" data-testid="push-toggle-row">
      <div className="pr-3">
        <p className="font-['Manrope'] font-semibold text-[#36454F] text-sm mb-0.5 flex items-center">
          {enabled ? <Check className="w-4 h-4 mr-1.5 text-[#B76E79]" strokeWidth={2} /> : <Bell className="w-4 h-4 mr-1.5 text-[#B76E79]" strokeWidth={1.5} />}
          Push reminders {enabled ? 'are on' : ''}
        </p>
        <p className="text-xs text-[#36454F]/55 font-['Manrope']">
          Day 30 + day 35 pings on your open disputes. Nothing else.
        </p>
      </div>
      <Button
        onClick={toggle}
        disabled={busy}
        variant={enabled ? 'outline' : 'default'}
        className={enabled
          ? "h-9 border-[#36454F]/25 text-[#36454F] hover:bg-[#36454F]/5 font-['Manrope'] rounded-sm text-sm"
          : "h-9 bg-[#B76E79] hover:bg-[#9e5c66] text-white font-['Manrope'] rounded-sm text-sm"}
        data-testid="push-toggle-btn"
      >
        {busy ? '…' : (enabled ? 'Turn off' : 'Turn on')}
      </Button>
    </div>
  );
}


/**
 * A rich preferences panel: choose milestone days, expense-alert threshold,
 * weekly digest opt-in, plus buttons to fire a test push and preview the
 * digest email. Renders only when Web Push is supported by the browser.
 */
export function PushReminderPreferences() {
  const [supported, setSupported] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [prefs, setPrefs] = useState(null);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [milestonesInput, setMilestonesInput] = useState('30, 35');

  useEffect(() => {
    (async () => {
      if (!isPushSupported()) { setSupported(false); setLoading(false); return; }
      try {
        const [p, enabled] = await Promise.all([getPushPreferences(), fetchPushStatus()]);
        setPrefs(p);
        setPushEnabled(enabled);
        setMilestonesInput((p.dispute_milestones || [30, 35]).join(', '));
      } catch {
        toast.error('Could not load reminder preferences.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (!supported || loading || !prefs) return null;

  const savePrefs = async (patch) => {
    setSaving(true);
    try {
      // Parse milestones from the text input; drop invalid
      const parsed = milestonesInput
        .split(/[,\s]+/)
        .map((s) => parseInt(s, 10))
        .filter((n) => Number.isFinite(n) && n >= 14 && n <= 90);
      const merged = {
        dispute_milestones: parsed.length ? Array.from(new Set(parsed)).sort((a, b) => a - b) : [30, 35],
        expense_alert_enabled: prefs.expense_alert_enabled,
        expense_alert_threshold: prefs.expense_alert_threshold,
        weekly_digest_enabled: prefs.weekly_digest_enabled,
        ...patch,
      };
      const updated = await updatePushPreferences(merged);
      setPrefs(updated);
      setMilestonesInput(updated.dispute_milestones.join(', '));
      toast.success('Preferences saved.');
    } catch {
      toast.error('Could not save preferences.');
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    try {
      if (!pushEnabled) {
        await enablePush();
        setPushEnabled(true);
      }
      const res = await sendTestPush();
      if (res?.sent > 0) toast.success(`Test ping sent to ${res.sent} device${res.sent > 1 ? 's' : ''}.`);
      else toast.info('No active devices found. Enable notifications on this device first.');
    } catch (err) {
      if (err?.code === 'permission-denied') {
        toast.error('Enable browser notifications first.');
      } else {
        toast.error('Could not send test ping.');
      }
    } finally {
      setTesting(false);
    }
  };

  const preview = async () => {
    setPreviewing(true);
    try {
      const res = await previewWeeklyDigest();
      toast.success(`Digest sent to ${res.email}. Check your inbox in ~30 seconds.`);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Could not send digest preview.';
      toast.error(detail);
    } finally {
      setPreviewing(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="push-preferences">
      {/* Reminder days */}
      <div className="border border-[#36454F]/10 rounded-sm p-4 bg-white">
        <Label htmlFor="milestones-input" className="font-['Manrope'] font-semibold text-[#36454F] text-sm">
          Dispute reminder days
        </Label>
        <p className="text-xs text-[#36454F]/55 font-['Manrope'] mt-1 mb-3">
          Comma-separated days after you file. Default: <code>30, 35</code>. Valid range: 14&ndash;90.
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          <Input
            id="milestones-input"
            value={milestonesInput}
            onChange={(e) => setMilestonesInput(e.target.value)}
            placeholder="30, 35"
            className="w-48 h-9 font-['Manrope'] text-sm border-[#36454F]/15 focus:border-[#B76E79] focus:ring-[#B76E79]"
            data-testid="milestones-input"
          />
          <Button
            onClick={() => savePrefs({})}
            disabled={saving}
            className="h-9 bg-[#B76E79] hover:bg-[#9e5c66] text-white font-['Manrope'] rounded-sm text-sm"
            data-testid="save-milestones-btn"
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>

      {/* Expense alert threshold */}
      <div className="border border-[#36454F]/10 rounded-sm p-4 bg-white">
        <div className="flex items-center justify-between mb-2">
          <div className="pr-4">
            <p className="font-['Manrope'] font-semibold text-[#36454F] text-sm">Large-expense alerts</p>
            <p className="text-xs text-[#36454F]/55 font-['Manrope']">
              Ping your household when someone logs an expense over your threshold.
            </p>
          </div>
          <Button
            onClick={() => savePrefs({ expense_alert_enabled: !prefs.expense_alert_enabled })}
            disabled={saving}
            variant={prefs.expense_alert_enabled ? 'outline' : 'default'}
            className={prefs.expense_alert_enabled
              ? "h-8 border-[#36454F]/25 text-[#36454F] hover:bg-[#36454F]/5 font-['Manrope'] rounded-sm text-xs"
              : "h-8 bg-[#B76E79] hover:bg-[#9e5c66] text-white font-['Manrope'] rounded-sm text-xs"}
            data-testid="toggle-expense-alerts"
          >
            {prefs.expense_alert_enabled ? 'On' : 'Off'}
          </Button>
        </div>
        {prefs.expense_alert_enabled && (
          <div className="flex items-center gap-2 mt-2">
            <span className="font-['Manrope'] text-sm text-[#36454F]/70">Threshold: $</span>
            <Input
              type="number"
              min="1"
              max="1000000"
              defaultValue={prefs.expense_alert_threshold}
              onBlur={(e) => {
                const v = parseFloat(e.target.value);
                if (Number.isFinite(v) && v !== prefs.expense_alert_threshold) {
                  savePrefs({ expense_alert_threshold: v });
                }
              }}
              className="w-24 h-8 font-['Manrope'] text-sm border-[#36454F]/15"
              data-testid="expense-threshold-input"
            />
          </div>
        )}
      </div>

      {/* Weekly digest */}
      <div className="border border-[#36454F]/10 rounded-sm p-4 bg-white">
        <div className="flex items-center justify-between">
          <div className="pr-4">
            <p className="font-['Manrope'] font-semibold text-[#36454F] text-sm flex items-center">
              <Mail className="w-4 h-4 mr-1.5 text-[#B76E79]" strokeWidth={1.5} />
              Sunday email digest
            </p>
            <p className="text-xs text-[#36454F]/55 font-['Manrope']">
              A gentle Sunday recap: open disputes, giving total, and a scripture pick.
            </p>
          </div>
          <Button
            onClick={() => savePrefs({ weekly_digest_enabled: !prefs.weekly_digest_enabled })}
            disabled={saving}
            variant={prefs.weekly_digest_enabled ? 'outline' : 'default'}
            className={prefs.weekly_digest_enabled
              ? "h-8 border-[#36454F]/25 text-[#36454F] hover:bg-[#36454F]/5 font-['Manrope'] rounded-sm text-xs"
              : "h-8 bg-[#B76E79] hover:bg-[#9e5c66] text-white font-['Manrope'] rounded-sm text-xs"}
            data-testid="toggle-weekly-digest"
          >
            {prefs.weekly_digest_enabled ? 'On' : 'Off'}
          </Button>
        </div>
        <Button
          onClick={preview}
          disabled={previewing || !prefs.weekly_digest_enabled}
          variant="ghost"
          className="mt-3 h-8 text-[#B76E79] hover:bg-[#B76E79]/8 font-['Manrope'] rounded-sm text-xs pl-2 pr-3"
          data-testid="preview-digest-btn"
        >
          {previewing ? (
            <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" strokeWidth={2} /> Sending…</>
          ) : (
            <><Send className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} /> Send me a preview</>
          )}
        </Button>
      </div>

      {/* Send test push */}
      <div className="border border-[#36454F]/10 rounded-sm p-4 bg-white">
        <p className="font-['Manrope'] font-semibold text-[#36454F] text-sm mb-1 flex items-center">
          <Bell className="w-4 h-4 mr-1.5 text-[#B76E79]" strokeWidth={1.5} />
          Verify delivery
        </p>
        <p className="text-xs text-[#36454F]/55 font-['Manrope'] mb-3">
          Send a test push to this device to make sure notifications are landing.
        </p>
        <Button
          onClick={test}
          disabled={testing}
          className="h-9 bg-[#B76E79] hover:bg-[#9e5c66] text-white font-['Manrope'] rounded-sm text-sm"
          data-testid="send-test-push-btn"
        >
          {testing ? (
            <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" strokeWidth={2} /> Sending…</>
          ) : (
            <><Send className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Send me a test ping</>
          )}
        </Button>
      </div>
    </div>
  );
}

